'use client';

import { contentImageAlt } from '@communio/content-image-alt';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { LoginLink } from '@/platform/auth/LoginLink';
import { useAuth } from '@/platform/auth/useAuth';
import {
  formatOrderPrice,
  formatShippingAddress,
  getOrder,
  reorderIntoCart,
  type VendorOrder,
} from './api';
import { MarketplaceOrderDisclaimer } from './MarketplaceOrderDisclaimer';
import { OrderMessageThread } from './OrderMessageThread';

function formatWhen(iso?: string) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function isCompletedOrder(status: VendorOrder['status']) {
  return status === 'paid' || status === 'processing' || status === 'shipped' || status === 'delivered';
}

type Props = {
  orderId: string;
};

export function BuyerOrderDetail({ orderId }: Props) {
  const router = useRouter();
  const { isAuthenticated, user } = useAuth();
  const [order, setOrder] = useState<VendorOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reorderLoading, setReorderLoading] = useState(false);
  const [reorderNotice, setReorderNotice] = useState('');

  useEffect(() => {
    getOrder(orderId)
      .then(setOrder)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [orderId]);

  const canComposeThread =
    !!order &&
    isAuthenticated &&
    !!user?.uid &&
    order.buyerUserId === user.uid;

  async function buyAgain() {
    if (!order) return;
    setReorderLoading(true);
    setReorderNotice('');
    setError('');
    try {
      const result = await reorderIntoCart(order.sellerUserId, order.id);
      if (result.skipped?.length) {
        setReorderNotice(
          `Added ${result.added?.length ?? 0} item(s). Skipped: ${result.skipped.map((s) => s.reason).join('; ')}`,
        );
      }
      router.push(`/marketplace/sellers/${order.sellerUserId}/?cart=1`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not re-order');
    } finally {
      setReorderLoading(false);
    }
  }

  if (loading) return <p>Loading order…</p>;
  if (error && !order) return <p className="form-error">{error}</p>;
  if (!order) return <p>Order not found.</p>;

  const showBuyAgain =
    isCompletedOrder(order.status) || order.status === 'cancelled' || order.status === 'refunded';

  return (
    <section className="buyer-order-detail feed-card settings-page">
      <p>
        <Link href="/marketplace/orders/">← Back to orders</Link>
      </p>
      <MarketplaceOrderDisclaimer />
      <p>
        Order <strong>{order.id.slice(0, 8)}</strong>
      </p>
      <p>
        Status: <strong>{order.status.replace(/_/g, ' ')}</strong>
      </p>
      {formatWhen(order.createdAt) && <p className="text-muted">Placed {formatWhen(order.createdAt)}</p>}
      {formatWhen(order.paidAt) && <p className="text-muted">Paid {formatWhen(order.paidAt)}</p>}
      {formatWhen(order.shippedAt) && <p className="text-muted">Shipped {formatWhen(order.shippedAt)}</p>}
      {formatWhen(order.deliveredAt) && <p className="text-muted">Delivered {formatWhen(order.deliveredAt)}</p>}

      {order.status === 'pending_payment' && (
        <p>
          <Link
            href={`/marketplace/sellers/${order.sellerUserId}/?cart=1`}
            className="btn btn-primary"
          >
            Continue in cart
          </Link>
        </p>
      )}

      <h2 className="buyer-order-detail__section">Items</h2>
      <ul>
        {order.lineItems.map((item) => (
          <li key={item.productId} className="buyer-order-detail__line">
            {item.imageUrl && (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={item.imageUrl} alt={contentImageAlt(item.name, 'product photo')} className="order-line__thumb" loading="lazy" />
            )}
            <span>
              {item.name} × {item.quantity} —{' '}
              {formatOrderPrice(item.unitPriceCents * item.quantity, order.currency)}
            </span>
          </li>
        ))}
      </ul>

      <h2 className="buyer-order-detail__section">Totals</h2>
      <p>Subtotal: {formatOrderPrice(order.subtotalCents, order.currency)}</p>
      {order.shippingCents != null && order.shippingCents > 0 && (
        <p>
          Shipping{order.shippingOptionLabel ? ` (${order.shippingOptionLabel})` : ''}:{' '}
          {formatOrderPrice(order.shippingCents, order.currency)}
        </p>
      )}
      {order.taxCents != null && order.taxCents > 0 && (
        <p>Tax: {formatOrderPrice(order.taxCents, order.currency)}</p>
      )}
      {order.platformFeeCents > 0 && (
        <p className="text-muted">Platform fee: {formatOrderPrice(order.platformFeeCents, order.currency)}</p>
      )}
      <p>
        <strong>Total: {formatOrderPrice(order.totalCents, order.currency)}</strong>
      </p>

      {order.shippingAddress && (
        <>
          <h2 className="buyer-order-detail__section">Shipping address</h2>
          <pre className="buyer-order-detail__address">{formatShippingAddress(order.shippingAddress)}</pre>
        </>
      )}

      {order.trackingNumber && (
        <p>
          Tracking: {order.trackingCarrier} {order.trackingNumber}
        </p>
      )}

      {showBuyAgain && (
        <p>
          <button type="button" className="btn btn-secondary" disabled={reorderLoading} onClick={() => void buyAgain()}>
            {reorderLoading ? 'Loading cart…' : 'Buy again'}
          </button>
        </p>
      )}
      {reorderNotice && <p className="text-muted">{reorderNotice}</p>}
      {error && <p className="form-error">{error}</p>}

      <p className="text-muted">
        Seller:{' '}
        <Link href={`/marketplace/sellers/${order.sellerUserId}/`}>View seller store</Link>
        {' · '}
        <Link href={`/users/${order.sellerUserId}/`}>Member profile</Link>
      </p>

      <OrderMessageThread
        orderId={order.id}
        canCompose={canComposeThread}
        guestHint="Sign in to message the seller about this order."
      />

      {!isAuthenticated && (
        <p>
          <LoginLink className="btn btn-ghost" returnTo={`/marketplace/orders/${order.id}/`}>
            Sign in to save this order to your history
          </LoginLink>
        </p>
      )}
    </section>
  );
}
